import { useRouter } from 'next/router';
import { Box } from '@zoopla-eng/zds/box';
import { Grid } from '@zoopla-eng/zds/grid';
import { useIsLoggedIn } from '@zoopla/user';
import { ZooplaLogo } from '@zoopla/assets';
import { SaveMedium, UserMedium, HomeMedium } from '@zoopla-eng/zds/icons';
import { ButtonLink } from '@zoopla-eng/zds/button';
import { Link, LinkWithIcon } from '@zoopla-eng/zds/link';
import Menu from './Menu';
import { getSigninUrl, Navigation, trackAccount, trackNav } from './data';
import {
    UpdateIndicator,
    useUnseenNotificationCount,
    largeScreenNotificationsFeature,
    mojoNotificationFlag
} from '@zoopla/notifications';
import { useIsHomeOwner } from './graphql/useIsHomeOwner';
import { useLazyOptimizelyFeatureFlag } from '@zoopla/optimizely';
import {
    actionIndicatorWrapperStyle,
    actionListItemStyle,
    actionListStyle,
    containerStyle,
    linkListStyle,
    linksContainerStyle,
    logoBoxStyle,
    logoContainerStyle,
    menuContainerStyle,
    menuItemIndicatorWrapperStyle,
    menuItemStyle,
    navStyle,
    skipToContentStyle
} from './Header.styles.css';
import { fontGroups } from '@zoopla-eng/zds/themes';

export default function Header() {
    const { asPath } = useRouter();
    const isLoggedIn = useIsLoggedIn();
    const signinUrl = getSigninUrl(asPath);

    const [, largeScreenNotificationsEnabled] = useLazyOptimizelyFeatureFlag(
        largeScreenNotificationsFeature
    );

    const [isLoadingMojoNotificationFlag, showMojoNotification] =
        useLazyOptimizelyFeatureFlag(mojoNotificationFlag);

    const displayNotificationsIcon = useIsHomeOwner(!isLoggedIn);

    const [unseenNotificationsCount, setLastSeenDate] =
        useUnseenNotificationCount(
            !(displayNotificationsIcon && !isLoadingMojoNotificationFlag),
            showMojoNotification
        );

    const hasUnseenNotifications =
        unseenNotificationsCount && unseenNotificationsCount > 0;

    return (
        <div className={containerStyle}>
            <div className={skipToContentStyle}>
                <ButtonLink href="#main-content" category="primary">
                    Skip to main content
                </ButtonLink>
            </div>
            <nav
                className={navStyle}
                aria-label="main"
                data-testid={`header`}
                data-is-logged-in={isLoggedIn ? 'true' : undefined}
            >
                <div className={logoContainerStyle}>
                    <div className={logoBoxStyle}>
                        <a
                            href="/"
                            aria-label="Zoopla's Homepage"
                            data-testid="zoopla-logo"
                        >
                            <ZooplaLogo />
                        </a>
                    </div>
                </div>
                <div className={menuContainerStyle}>
                    <Box bpL100={{ display: 'none' }}>
                        <Menu isLoggedIn={isLoggedIn} signinUrl={signinUrl} />
                    </Box>
                    <div className={linksContainerStyle}>
                        <ul className={`${linkListStyle} ${fontGroups.ladle}`}>
                            {Navigation.map(({ title, href }, idx) => (
                                <li
                                    key={href}
                                    data-testid={`header-menu${idx}`}
                                >
                                    <Link
                                        href={href}
                                        onClick={trackNav(title)}
                                        muted
                                        disableVisited
                                    >
                                        {title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <Grid.Item area="headerright">
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        bpL100={{ display: 'none' }}
                    >
                        <ul
                            className={`${actionListStyle} ${fontGroups.zester}`}
                        >
                            <li className={actionListItemStyle}>
                                <LinkWithIcon
                                    onClick={() => {
                                        trackAccount('my home')();
                                        setLastSeenDate();
                                    }}
                                    href="/my-home/"
                                    iconPrefix={<HomeMedium title="My Home" />}
                                    stacked
                                    muted
                                    disableVisited
                                    data-testid="my-home-mobile-link"
                                >
                                    My Home
                                    {hasUnseenNotifications ? (
                                        <div
                                            className={
                                                actionIndicatorWrapperStyle
                                            }
                                        >
                                            <UpdateIndicator
                                                data-testid="header-notifications-new"
                                                count={
                                                    hasUnseenNotifications
                                                        ? String(
                                                              unseenNotificationsCount
                                                          )
                                                        : undefined
                                                }
                                                type={
                                                    hasUnseenNotifications
                                                        ? 'navVariant'
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    ) : null}
                                </LinkWithIcon>
                            </li>
                            <li className={actionListItemStyle}>
                                <LinkWithIcon
                                    onClick={trackAccount(
                                        'favourite properties'
                                    )}
                                    href="/myaccount/favourites/"
                                    iconPrefix={<SaveMedium title="Saved" />}
                                    stacked
                                    muted
                                    disableVisited
                                >
                                    Saved
                                </LinkWithIcon>
                            </li>
                            {!isLoggedIn && (
                                <li className={actionListItemStyle}>
                                    <LinkWithIcon
                                        onClick={trackAccount('sign in')}
                                        href={signinUrl}
                                        iconPrefix={
                                            <UserMedium title="Sign in" />
                                        }
                                        stacked
                                        muted
                                        disableVisited
                                    >
                                        Sign in
                                    </LinkWithIcon>
                                </li>
                            )}
                        </ul>
                    </Box>
                    <Box
                        display="none"
                        justifyContent="flex-end"
                        bpL100={{ display: 'flex' }}
                    >
                        <ul className={`${linkListStyle} ${fontGroups.ladle}`}>
                            <li className={menuItemStyle}>
                                <Link
                                    href="/my-home/"
                                    onClick={() => {
                                        trackAccount('my home')();
                                        if (largeScreenNotificationsEnabled) {
                                            setLastSeenDate();
                                        }
                                    }}
                                    muted
                                    disableVisited
                                    data-testid="my-home-desktop-link"
                                >
                                    My Home
                                    {largeScreenNotificationsEnabled &&
                                    hasUnseenNotifications ? (
                                        <div
                                            className={
                                                menuItemIndicatorWrapperStyle
                                            }
                                        >
                                            <UpdateIndicator
                                                data-testid="header-notifications-new"
                                                count={
                                                    hasUnseenNotifications
                                                        ? String(
                                                              unseenNotificationsCount
                                                          )
                                                        : undefined
                                                }
                                                type={
                                                    hasUnseenNotifications
                                                        ? 'navVariant'
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    ) : null}
                                </Link>
                            </li>
                            <li className={menuItemStyle}>
                                <Link
                                    href="/myaccount/favourites/"
                                    onClick={trackAccount(
                                        'favourite properties'
                                    )}
                                    muted
                                    disableVisited
                                >
                                    Saved
                                </Link>
                            </li>
                            {isLoggedIn ? (
                                <li className={menuItemStyle}>
                                    <Link
                                        href="/my-zoopla/"
                                        onClick={trackAccount()}
                                        muted
                                        disableVisited
                                    >
                                        Account
                                    </Link>
                                </li>
                            ) : (
                                <li className={menuItemStyle}>
                                    <Link
                                        href={signinUrl}
                                        onClick={trackAccount('sign in')}
                                        muted
                                        disableVisited
                                    >
                                        Sign in
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </Box>
                </Grid.Item>
            </nav>
        </div>
    );
}
